/* eslint-disable no-console */
// Util compartido de desvinculación de pagos. Extrae la lógica core que vivía acoplada a
// VincularMovimientoModal (handleDesvincularSII / handleDesvincularCaja) para que también la
// pueda invocar EstadoCuenta desde las filas de pago del ledger (ver pendiente 9.9).
//
// Patrón fire-and-pray del codebase: cada paso loguea su error pero no aborta el resto
// (preferimos completar la desvinculación aunque queden refs huérfanas que limpiar después).
// El client se recibe por parámetro (default al singleton) para facilitar testing.

import { client } from "../supabase";

// Campos que devuelven un movimiento bancario a 'pendiente', limpiando FKs y match_*.
// Mismo shape que el UPDATE de handleDesvincularSII (incluye las FKs directas de SII).
const RESET_BANCARIO = {
  factura_sii_emitida_id: null,
  factura_sii_recibida_id: null,
  movimiento_caja_id: null,
  estado: "pendiente",
  procesado_por: null,
  procesado_at: null,
  match_factura_tipo: null,
  match_factura_id: null,
  match_entidad_tipo: null,
  match_entidad_id: null,
  match_entidad_nombre: null,
  match_confianza: null,
  match_razon: null,
};

// Desvincula un pago con vinculación directa vía MBF (equivale a handleDesvincularSII al borrar
// la última fila): borra MBF, resetea el banco, borra la caja y libera las facturas externas.
export const desvincularPagoMBF = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  try {
    // 1. SELECT filas MBF del movimiento + 2. capturar externas antes del DELETE.
    let externasLiberadas = [];
    try {
      const { data: filas, error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .select("factura_tipo, factura_id")
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
      externasLiberadas = (filas || []).filter((f) => f.factura_tipo === "factura_externa").map((f) => f.factura_id);
    } catch (err) {
      console.error("desvincularPagoMBF: error leyendo filas MBF:", err);
    }

    // 3. DELETE todas las filas MBF del movimiento.
    try {
      const { error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
    } catch (err) {
      console.error("desvincularPagoMBF: error borrando filas MBF:", err);
    }

    // 4. UPDATE banco a pendiente (FKs + match_* limpios).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error reseteando movimiento bancario:", err);
      }
    }

    // 5. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error borrando movimiento de caja:", err);
      }
    }

    // 6. Liberar facturas externas vinculadas (vuelven a pendiente / disponibles para revincular).
    if (externasLiberadas.length > 0) {
      try {
        const { error } = await supabaseClient
          .from("facturas_externas")
          .update({ estado: "pendiente", movimiento_bancario_id: null })
          .in("id", externasLiberadas);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error liberando facturas externas:", err);
      }
    }

    return { ok: true, externasLiberadas };
  } catch (err) {
    return { ok: false, error: err };
  }
};

// Desvincula un pago que vive solo en caja (sin MBF) — equivale a handleDesvincularCaja:
// resetea el banco a pendiente (si lo hay) y borra la caja. No toca facturas_externas.
export const desvincularPagoCaja = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  try {
    // 1. UPDATE banco a pendiente (solo si el pago tiene movimiento bancario asociado).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error reseteando movimiento bancario:", err);
      }
    }

    // 2. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error borrando movimiento de caja:", err);
      }
    }

    return { ok: true };
  } catch (err) {
    return { ok: false, error: err };
  }
};
